import { Box } from '@mui/material';
import { createTheme } from '@mui/material/styles';

/* ══════════════════════════════════════════════════════
   BRAND PALETTE — sampled directly from the RideX City logo
   Navy  = the "RIDE" wordmark   ·   Green = the "X" blade
══════════════════════════════════════════════════════ */
export const NAVY        = '#0B2A5C';  // primary
export const NAVY_DARK   = '#061A3B';  // deepest navy (footer / hero bg)
export const NAVY_DEEP   = '#081F49';
export const NAVY_LIGHT  = '#1E4C99';  // lighter navy for hovers/gradients
export const GREEN       = '#79AB1D';  // accent (matches logo green exactly)
export const GREEN_DARK  = '#5C8814';
export const GREEN_LIGHT = '#A4D65E';
export const INK         = '#0B1220';  // near-black text
export const PAPER       = '#F5F7FA';
export const AMBER       = '#B9770E';  // used sparingly for "cost/expense" data rows

/* Back-compat short aliases so older component code keeps working */
export const P  = NAVY;
export const PD = NAVY_DARK;
export const PL = NAVY_LIGHT;
export const A  = GREEN;
export const AD = GREEN_DARK;

export const PHONE = '+91 81231 98675';
export const PHONE_DIGITS = PHONE.replace(/\D/g, '');

/* ══════════════════════════════════════════════════════
   SIGNATURE MOTIF — angled "speed-cut" corners
   Echoes the twin diagonal blades of the X in the logo.
   Used consistently on cards, tiles and hero CTAs instead
   of generic rounded rectangles.
══════════════════════════════════════════════════════ */
export const cutCorners = (size = 18) => ({
  clipPath: `polygon(0 0, calc(100% - ${size}px) 0, 100% ${size}px, 100% 100%, ${size}px 100%, 0 calc(100% - ${size}px))`,
});
export const cutCornerTR = (size = 16) => ({
  clipPath: `polygon(0 0, calc(100% - ${size}px) 0, 100% ${size}px, 100% 100%, 0 100%)`,
});

/* Twin diagonal "blade" graphic — the decorative echo of the logo's X */
export function SpeedSlashes({ color = GREEN, opacity = 0.14, sx = {} }) {
  return (
    <Box aria-hidden sx={{ position: 'absolute', pointerEvents: 'none', ...sx }}>
      <Box sx={{
        width: { xs: 130, md: 200 }, height: { xs: 10, md: 15 },
        bgcolor: color, opacity, transform: 'rotate(-32deg)',
        clipPath: 'polygon(0 40%, 94% 0, 100% 25%, 6% 100%)',
      }} />
      <Box sx={{
        width: { xs: 130, md: 200 }, height: { xs: 6, md: 9 },
        bgcolor: color, opacity: opacity * 0.75, transform: 'rotate(-32deg) translate(16px, 24px)',
        clipPath: 'polygon(0 40%, 94% 0, 100% 25%, 6% 100%)',
      }} />
    </Box>
  );
}

export const theme = createTheme({
  palette: {
    primary: { main: NAVY, dark: NAVY_DARK, light: NAVY_LIGHT, contrastText: '#fff' },
    secondary: { main: GREEN, dark: GREEN_DARK, light: GREEN_LIGHT, contrastText: '#fff' },
    background: { default: PAPER, paper: '#ffffff' },
    text: { primary: INK, secondary: '#51607a' },
  },
  typography: {
    fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
    h1: { fontFamily: '"Barlow Condensed","Inter",sans-serif', fontWeight: 800, letterSpacing: '-0.01em', textTransform: 'none' },
    h2: { fontFamily: '"Barlow Condensed","Inter",sans-serif', fontWeight: 800, letterSpacing: '-0.005em' },
    h3: { fontFamily: '"Barlow Condensed","Inter",sans-serif', fontWeight: 700 },
    h4: { fontFamily: '"Barlow Condensed","Inter",sans-serif', fontWeight: 700 },
    h5: { fontFamily: '"Barlow Condensed","Inter",sans-serif', fontWeight: 700 },
    h6: { fontFamily: '"Barlow Condensed","Inter",sans-serif', fontWeight: 700, letterSpacing: '0.01em' },
    overline: { fontFamily: '"Inter","Roboto",sans-serif', letterSpacing: '0.14em', fontWeight: 800 },
    button: { fontFamily: '"Inter",sans-serif' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 700, borderRadius: 8 },
      },
    },
    MuiCard: { styleOverrides: { root: { borderRadius: 16 } } },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter",sans-serif', letterSpacing: '0.06em',
          textTransform: 'uppercase', fontSize: '0.72rem', fontWeight: 800,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&:hover fieldset': { borderColor: NAVY },
            '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: 2 },
          },
          '& label.Mui-focused': { color: NAVY },
        },
      },
    },
  },
});
